<template>
  <div class="permitions-tab">
    <div class="permitions-tab__field permitions-tab__field--col">
      <file-permition-select
        v-model="selectedPerson"
        class="permitions-tab__dropdown"
        dropdown-class="select-dropdown--above-modal"
        placeholder="Выбрать пользователей"
        :items="optionsPersons"
        :min-dropdown-width="665"
        :dropdown-height="600"
        :disabled="ruleAccess == 2 ? false : true"
        searchable
        multiple
        @open="openDropdown"
        @close="closeDropdown"
        :dropdown-label="dropdownLabel"
      >
        <template
          #dropdown="{ items, selected, select, getGroupState, search }"
        >
          <UserGroupDropdownContent
            :items="items"
            :selected="selected"
            :select="select"
            :get-group-state="getGroupState"
            :updateSelected="updateSelected"
            :search="search"
          />
        </template>
      </file-permition-select>
      <file-permitions-user-group-list
        v-if="selectedDisplayList.length !== 0"
        :groups="selectedDisplayList"
        :disabled="ruleAccess !==2"
        @remove="handleRemoveSelected"
      />
    </div>

    <div
      v-if="fileInfo?.Changelog?.length"
      class="permitions-tab__field permitions-tab__field--col"
    >
      <div class="permitions-tab__field-name">Изменен:</div>
      <div
        v-for="(change, index) in showAllChanges
          ? fileInfo.Changelog
          : fileInfo.Changelog.slice(0, 4)"
        class="permitions-tab__field-value-col"
        :key="change.Time"
      >
        <span class="permitions-tab__field-value-col-time"
          >{{ getDate(change.Time) }}&nbsp;</span
        >
        <span class="permitions-tab__field-value-col-name"
          >[{{ change.Changer }}]</span
        >
        <span class="permitions-tab__field-value-col-action">{{
          change.Text
        }}</span>
        <div
          v-if="!showAllChanges && fileInfo.Changelog.length > 4 && index === 3"
          class="permitions-tab__field-value-col-show-more"
          @click="showAllChanges = true"
        >
          ещё {{ fileInfo.Changelog.length - 4 }}
        </div>
      </div>
    </div>
  </div>
  <div class="permitions-tab__line" />
  <div class="permitions-tab__actions">
    <app-button
      type="primary"
      label="Сохранить"
      @click="save"
      :disabled="!hasChanges || isSaving"
    />
  </div>
</template>

<script setup lang="ts">
import FilePermitionSelect from "../fileUi/FilePermitionSelect.vue";
import UserGroupDropdownContent from "@/components/permitions/UserGroupDropdownContent.vue";
import AppButton from "@/components/ui-kit/button/AppButton.vue";
import FilePermitionsUserGroupList from "./FilePermitionsUserGroupList.vue";
import { computed, onMounted, ref, watch } from "vue";
import type {
  SelectOption,
} from "../../ui-kit/select/AppSelect.vue";
import moment from "moment";
import { FileApi } from "@/ api/FileApi";
import { WorkersAndAccessGroupsInSettingsGiver } from "@/models/interfaces/access/entity-rules";
import { AccessApi } from "@/ api/AccessApi";

type Props = {
  fileInfo?: any;
  ruleAccess: number;
  entityId: number      
  clientId: number       
  projectId: number      
  isFolder?: boolean
};

const props = withDefaults(defineProps<Props>(), {
  ruleAccess: 0,
  isFolder: false,
});

const emit = defineEmits(["save", "openDropdown", "closeDropdown"]);

const groupsRules = ref<WorkersAndAccessGroupsInSettingsGiver>();
const initialGroups = ref<(string | number)[]>([])
const dropdownLabel = computed(() => {
  return `Выбрано: ${selectedDisplayList.value.length} пользователей`;
});

const selectedPerson = ref([]);
const showAllChanges = ref(false);
const isSaving = ref(false);

onMounted(async () => {
  groupsRules.value = await AccessApi.getWorkersAndAccessGroups();
});

const openDropdown = () => {
  emit("openDropdown");
};

const closeDropdown = () => {
  emit("closeDropdown");
};

watch(
  () => props.fileInfo,
  (newVal) => {
    const ids = newVal?.GroupsWithAccess?.length
      ? newVal.GroupsWithAccess.map((g: any) => g.Id)
      : []
    selectedPerson.value = ids
    initialGroups.value = [...ids]
  },
  { immediate: true },
)

const hasChanges = computed(() => {
  if (selectedPerson.value.length !== initialGroups.value.length) return true
  const initialSet = new Set(initialGroups.value)
  return selectedPerson.value.some(id => !initialSet.has(id))
})

const updateSelected = (newSelected) => {
  selectedPerson.value = newSelected;
};

const save = async () => {
  if (isSaving.value) return
  if (!hasChanges.value) {
    emit('save')
    return
  }

  isSaving.value = true
  try {
    const groupIds = selectedPerson.value as number[]

    if (props.isFolder) {
      await FileApi.EditFolderAccess(
        props.entityId,
        groupIds,
        props.clientId,
        props.projectId,
      )
    } else {
      await FileApi.EditFileAccess(
        props.entityId,
        groupIds,
        props.clientId,
        props.projectId,
      )
    }

    initialGroups.value = [...selectedPerson.value]
    emit('save')
  } catch (e) {
    console.error(e)
  } finally {
    isSaving.value = false
  }
}

const optionsPersons = computed<SelectOption[]>(() => {
  if (!groupsRules.value?.Holders.length) return [];

  const groups: SelectOption[] = [];
  const workersWithoutDepartment: {
    id: number;
    label: string;
    type: "item";
  }[] = [];

  groupsRules.value?.Holders.forEach((el, index) => {
    if (el.Departament) {
      groups.push({
        id: index + 999999,
        label: el.Departament.Name,
        type: "group" as const,
        selectable: true,
        children:
          el.Departament.Workers?.map((worker) => ({
            id: worker.GroupId,
            label: worker.Name,
            type: "item" as const,
          })) ?? [],
      });
    } else if (el.IsWorker && el.Worker) {
      workersWithoutDepartment.push({
        id: el.Worker.GroupId,
        label: el.Worker.Name,
        type: "item" as const,
      });
    }
  });

  groupsRules.value?.Groups.forEach((el, index) => {
    if (!el.IsPersonal) {
      groups.push({
        id: el.Id,
        label: el.Name,
        type: "group" as const,
        selectable: true,
        children: [],
      });
    }
  });

  if (workersWithoutDepartment.length > 0) {
    groups.push({
      id: -1,
      label: "Без отдела",
      type: "group" as const,
      selectable: false,
      children: workersWithoutDepartment,
    });
  }

  return groups;
});

const selectedDisplayList = computed(() => {
  return selectedPerson.value
    .map((id) => {
      for (const item of optionsPersons.value) {
        if (item.type === 'group' && 'children' in item && item.children) {
          const child = item.children.find((child) => child.id === id)
          if (child) return { Id: child.id, Name: child.label, type: 'item' }
        }
        if (item.id === id) return { Id: item.id, Name: item.label, type: item.type }
      }

      const fromServer = props.fileInfo?.GroupsWithAccess?.find((g: any) => g.Id === id)
      if (fromServer) return { Id: fromServer.Id, Name: fromServer.Name, type: 'item' }

      return null
    })
    .filter(Boolean) as { Id: string | number; Name: string; type: string }[]
})

const handleRemoveSelected = (item: {
  Id: string | number;
  Name: string;
  type: string;
}) => {
  selectedPerson.value = selectedPerson.value.filter((id) => id !== item.Id);
};

const getDate = (date: string) => {
  return moment(date).format("HH:mm DD.MM.YYYY");
};
</script>

<style scoped lang="scss">
@import "@/styles/color.scss";

.permitions-tab {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 12px 16px;
  gap: 16px;
  flex: 1;

  &__dropdown {
    width: 250px;
  }

  &__field {
    margin-bottom: 4px;
    font-size: 14px;
    line-height: 18px;
    overflow-wrap: anywhere;
    word-break: break-word;

    &--col {
      display: flex;
      flex-direction: column;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    &-name {
      color: $color-text_main;
      font-weight: 500;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    &-value {
      color: $color-text_main;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    &-value-col {
      display: inline-block;
      align-items: center;
      overflow-wrap: anywhere;
      word-break: break-word;

      &-time {
        color: #979797;
      }

      &-name {
        color: $color-text_main;
      }

      &-action {
        margin-left: 2px;
        color: $color-text_main;
      }

      &-show-more {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 16px;
        padding: 2px 4px;
        font-size: 10px;
        border-radius: 8px;
        margin-left: 6px;
        margin-top: 1px;
        background-color: #f2f2f2;
        color: #979797;
        cursor: pointer;
      }

      &-link {
        font-size: 14px;
        line-height: 16px;
        color: #499259;
        text-decoration: none;
        margin-bottom: 4px;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  &__line {
    margin-top: auto;
    border-bottom: 1px solid $color-border;
  }

  &__actions {
    font-size: 14px;
    line-height: 18px;
    display: flex;
    gap: 8px;
    padding: 12px 16px;
    margin-left: auto;

    &-weight {
      font-weight: 500;
    }
  }
}
</style>
